import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import toast from 'react-hot-toast';
import {
    EnvelopeIcon,
    EnvelopeOpenIcon,
    TrashIcon,
    EyeIcon,
    CheckIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';

const AdminContactListPage = () => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, unread, read
    const [selectedContact, setSelectedContact] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [selectedIds, setSelectedIds] = useState([]);

    const fetchContacts = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params = {};
            
            if (filter === 'unread') {
                params.isRead = 'false';
            } else if (filter === 'read') {
                params.isRead = 'true';
            }

            const response = await axios.get(`${API_BASE_URL}/api/contact`, {
                headers: { 'x-auth-token': token },
                params
            });

            setContacts(response.data.contacts);
            setUnreadCount(response.data.unreadCount);
        } catch (error) {
            console.error('Error fetching contacts:', error);
            toast.error('Failed to load messages');
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        fetchContacts();
    }, [fetchContacts]);

    const viewContact = async (contact) => {
        setSelectedContact(contact);
        setShowModal(true);

        // Mark as read if it's unread
        if (!contact.isRead) {
            try {
                const token = localStorage.getItem('token');
                await axios.put(
                    `${API_BASE_URL}/api/contact/${contact._id}/read`,
                    {},
                    { headers: { 'x-auth-token': token } }
                );
                fetchContacts(); // Refresh list
            } catch (error) {
                console.error('Error marking as read:', error);
            }
        }
    };

    const toggleRead = async (id, currentStatus) => {
        try {
            const token = localStorage.getItem('token');
            const endpoint = currentStatus ? 'unread' : 'read';
            
            await axios.put(
                `${API_BASE_URL}/api/contact/${id}/${endpoint}`,
                {},
                { headers: { 'x-auth-token': token } }
            );
            
            toast.success(`Message marked as ${currentStatus ? 'unread' : 'read'}`);
            fetchContacts();
        } catch (error) {
            console.error('Error toggling read status:', error);
            toast.error('Failed to update message');
        }
    };

    const deleteContact = async (id) => {
        if (!window.confirm('Are you sure you want to delete this message?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_BASE_URL}/api/contact/${id}`, {
                headers: { 'x-auth-token': token }
            });

            toast.success('Message deleted successfully');
            setShowModal(false);
            fetchContacts();
        } catch (error) {
            console.error('Error deleting contact:', error);
            toast.error('Failed to delete message');
        }
    };

    const deleteSelected = async () => {
        if (selectedIds.length === 0) {
            toast.error('Please select messages to delete');
            return;
        }

        if (!window.confirm(`Delete ${selectedIds.length} message(s)?`)) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_BASE_URL}/api/contact/bulk/delete`,
                { ids: selectedIds },
                { headers: { 'x-auth-token': token } }
            );

            toast.success('Messages deleted successfully');
            setSelectedIds([]);
            fetchContacts();
        } catch (error) {
            console.error('Error deleting contacts:', error);
            toast.error('Failed to delete messages');
        }
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === contacts.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(contacts.map(c => c._id));
        }
    };

    const toggleSelect = (id) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
        } else {
            setSelectedIds([...selectedIds, id]);
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="container mx-auto px-6 py-8">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold mb-2">Contact Messages</h1>
                    <p className="text-light-text/70 dark:text-dark-text/70">
                        Manage customer inquiries and support requests
                    </p>
                </div>
                {unreadCount > 0 && (
                    <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-full">
                        {unreadCount} Unread
                    </div>
                )}
            </div>

            {/* Filters */}
            <div className="card p-4 mb-6">
                <div className="flex flex-wrap gap-4 items-center justify-between">
                    <div className="flex gap-2">
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                                filter === 'all'
                                    ? 'bg-light-primary dark:bg-dark-primary text-white'
                                    : 'bg-light-bg dark:bg-dark-bg'
                            }`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setFilter('unread')}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                                filter === 'unread'
                                    ? 'bg-light-primary dark:bg-dark-primary text-white'
                                    : 'bg-light-bg dark:bg-dark-bg'
                            }`}
                        >
                            Unread
                        </button>
                        <button
                            onClick={() => setFilter('read')}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                                filter === 'read'
                                    ? 'bg-light-primary dark:bg-dark-primary text-white'
                                    : 'bg-light-bg dark:bg-dark-bg'
                            }`}
                        >
                            Read
                        </button>
                    </div>

                    {selectedIds.length > 0 && (
                        <button
                            onClick={deleteSelected}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
                        >
                            <TrashIcon className="h-5 w-5" />
                            Delete Selected ({selectedIds.length})
                        </button>
                    )}
                </div>
            </div>

            {/* Messages List */}
            {loading ? (
                <div className="flex justify-center py-20">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-light-primary dark:border-dark-primary"></div>
                </div>
            ) : contacts.length === 0 ? (
                <div className="card p-12 text-center">
                    <EnvelopeIcon className="h-16 w-16 mx-auto mb-4 text-light-text/30 dark:text-dark-text/30" />
                    <h3 className="text-xl font-bold mb-2">No Messages</h3>
                    <p className="text-light-text/70 dark:text-dark-text/70">
                        {filter === 'unread' ? 'No unread messages' : filter === 'read' ? 'No read messages' : 'No messages yet'}
                    </p>
                </div>
            ) : (
                <div className="card overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-light-bg dark:bg-dark-bg">
                            <tr>
                                <th className="p-4 text-left">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.length === contacts.length}
                                        onChange={toggleSelectAll}
                                        className="w-4 h-4"
                                    />
                                </th>
                                <th className="p-4 text-left">Status</th>
                                <th className="p-4 text-left">Name</th>
                                <th className="p-4 text-left">Email</th>
                                <th className="p-4 text-left">Subject</th>
                                <th className="p-4 text-left">Date</th>
                                <th className="p-4 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {contacts.map((contact) => (
                                <tr
                                    key={contact._id}
                                    className={`border-t border-light-border dark:border-dark-border hover:bg-light-bg dark:hover:bg-dark-bg transition-colors ${
                                        !contact.isRead ? 'font-semibold' : ''
                                    }`}
                                >
                                    <td className="p-4">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(contact._id)}
                                            onChange={() => toggleSelect(contact._id)}
                                            className="w-4 h-4"
                                        />
                                    </td>
                                    <td className="p-4">
                                        {contact.isRead ? (
                                            <EnvelopeOpenIcon className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <EnvelopeIcon className="h-5 w-5 text-blue-500" />
                                        )}
                                    </td>
                                    <td className="p-4">{contact.name}</td>
                                    <td className="p-4">
                                        <a
                                            href={`mailto:${contact.email}`}
                                            className="text-light-primary dark:text-dark-primary hover:underline"
                                        >
                                            {contact.email}
                                        </a>
                                    </td>
                                    <td className="p-4">{contact.subject}</td>
                                    <td className="p-4 text-sm text-light-text/70 dark:text-dark-text/70">
                                        {formatDate(contact.createdAt)}
                                    </td>
                                    <td className="p-4">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => viewContact(contact)}
                                                className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                                                title="View"
                                            >
                                                <EyeIcon className="h-5 w-5 text-blue-500" />
                                            </button>
                                            <button
                                                onClick={() => toggleRead(contact._id, contact.isRead)}
                                                className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                                                title={contact.isRead ? 'Mark as unread' : 'Mark as read'}
                                            >
                                                {contact.isRead ? (
                                                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                                                ) : (
                                                    <CheckIcon className="h-5 w-5 text-green-500" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => deleteContact(contact._id)}
                                                className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                                                title="Delete"
                                            >
                                                <TrashIcon className="h-5 w-5 text-red-500" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* View Message Modal */}
            {showModal && selectedContact && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-light-card dark:bg-dark-card rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                    >
                        <div className="p-6 border-b border-light-border dark:border-dark-border">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h2 className="text-2xl font-bold mb-2">{selectedContact.subject}</h2>
                                    <div className="flex items-center gap-4 text-sm text-light-text/70 dark:text-dark-text/70">
                                        <span>From: {selectedContact.name}</span>
                                        <span>•</span>
                                        <a
                                            href={`mailto:${selectedContact.email}`}
                                            className="text-light-primary dark:text-dark-primary hover:underline"
                                        >
                                            {selectedContact.email}
                                        </a>
                                    </div>
                                    <div className="text-sm text-light-text/70 dark:text-dark-text/70 mt-1">
                                        {formatDate(selectedContact.createdAt)}
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            <h3 className="font-semibold mb-3">Message:</h3>
                            <p className="text-light-text/80 dark:text-dark-text/80 whitespace-pre-wrap leading-relaxed">
                                {selectedContact.message}
                            </p>
                        </div>

                        <div className="p-6 border-t border-light-border dark:border-dark-border flex gap-3">
                            <a
                                href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                                className="btn-primary flex-1"
                            >
                                Reply via Email
                            </a>
                            <button
                                onClick={() => deleteContact(selectedContact._id)}
                                className="btn-secondary flex items-center gap-2"
                            >
                                <TrashIcon className="h-5 w-5" />
                                Delete
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default AdminContactListPage;
